import { useSelector } from 'react-redux';
import { RootState } from '../store';

function parsePermissions(raw: any): Record<string, boolean> | string[] | null {
  if (!raw && raw !== false) return null;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') return raw as Record<string, boolean>;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) || typeof parsed === 'object') return parsed as any;
    } catch (e) {
      return null;
    }
  }
  return null;
}

export function resolveUserRoleName(user: unknown): string {
  if (!user) return '';
  const u = user as { role?: { name?: string }; roleName?: string };
  return (u.role?.name ?? u.roleName ?? '').toLowerCase();
}

/** Shared permission check for hooks and route guards (no Redux subscription). */
export function checkUserPermission(user: unknown, permission: string): boolean {
  if (!user) return false;
  const role = resolveUserRoleName(user);
  if (role === 'admin') return true;

  const u = user as any;
  const rawPerms: any = u.role?.permissions ?? u.permissions;
  const perms = parsePermissions(rawPerms);
  if (!perms) return false;

  if (Array.isArray(perms)) return (perms as string[]).includes(permission) || (perms as string[]).includes('all');
  const pObj = perms as Record<string, boolean>;
  return Boolean(pObj['all'] || pObj[permission]);
}

/** Route table / route KPI finance without finance module access. */
export function checkCanViewRouteFinance(user: unknown): boolean {
  if (!user) return false;
  const role = resolveUserRoleName(user);
  if (role === 'admin' || role === 'manager') return true;
  return (
    checkUserPermission(user, 'routes.finance_view') || checkUserPermission(user, 'finance.view')
  );
}

/** Finance module menu and /finance/* routes. */
export function checkCanViewFinanceModule(user: unknown): boolean {
  if (!user) return false;
  const role = resolveUserRoleName(user);
  if (role === 'admin' || role === 'manager') return true;
  return checkUserPermission(user, 'finance.view');
}

export function isReadOnlyRoleUser(user: unknown): boolean {
  return resolveUserRoleName(user) === 'manager';
}

export default function useHasPermission(permission: string): boolean {
  const user = useSelector((state: RootState) => state.auth.user);
  return checkUserPermission(user, permission);
}
